'use client';
import React, { useRef, useState } from 'react';
import Link from 'next/link';
import { Zap, ArrowRight, Play, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import Vapi from '@vapi-ai/web';

const trustBadges = [
  { label: 'Live browser demo', icon: CheckCircle },
  { label: 'Buyer-owned accounts', icon: CheckCircle },
  { label: 'No fake usage claims', icon: CheckCircle },
];

const statsData = [
  { value: 'Demo', label: 'Browser test call', sublabel: 'Works with your Vapi key' },
  { value: 'Setup', label: 'Vapi + Twilio flow', sublabel: 'Guided in the dashboard' },
  { value: 'BYOK', label: 'Buyer-owned billing', sublabel: 'No platform key required' },
];

export default function HeroSection() {
  const [demoLoading, setDemoLoading] = useState(false);
  const [live, setLive] = useState(false);
  const vapiRef = useRef<Vapi | null>(null);

  const handleLiveDemo = async () => {
    if (live && vapiRef.current) {
      vapiRef.current.stop();
      vapiRef.current = null;
      setLive(false);
      setDemoLoading(false);
      toast.info('Demo call ended.');
      return;
    }

    const publicKey = process.env.NEXT_PUBLIC_VAPI_PUBLIC_KEY;
    const assistantId = process.env.NEXT_PUBLIC_VAPI_DEMO_ASSISTANT_ID;
    if (!publicKey || !assistantId) {
      toast.error('Live demo is not configured yet.');
      return;
    }

    setDemoLoading(true);
    try {
      const vapi = new Vapi(publicKey);
      vapiRef.current = vapi;
      vapi.on('call-end', () => {
        setLive(false);
        setDemoLoading(false);
        vapiRef.current = null;
      });
      vapi.on('error', () => {
        setLive(false);
        setDemoLoading(false);
        vapiRef.current = null;
      });

      await vapi.start(assistantId);
      setLive(true);
      setDemoLoading(false);
      toast.success('Live demo call started. Allow microphone access to continue.');
    } catch (error: unknown) {
      setLive(false);
      setDemoLoading(false);
      vapiRef.current = null;
      toast.error(error instanceof Error ? error.message : 'Failed to start live demo.');
    }
  };

  return (
    <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-grid-pattern opacity-40" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full bg-accent/5 blur-3xl pointer-events-none" />
      <div className="relative max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Announcement badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium">
            <Zap size={14} className="fill-primary" />
            Powered by Vapi.ai voice intelligence
            <ArrowRight size={14} />
          </div>
        </div>

        {/* Headline */}
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight tracking-tight mb-6">
            Your business never misses{' '}
            <span className="text-gradient">a call again.</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-10">
            VoiceLink AI is your 24/7 AI voice receptionist — answers every call in under 2 seconds,
            transcribes conversations, detects caller sentiment, and sends automated SMS follow-ups.
            Built for small businesses.
          </p>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
            {trustBadges?.map((badge) => (
              <div key={`trust-${badge?.label}`} className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <badge.icon size={15} className="text-positive" />
                {badge?.label}
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link
              href="/sign-up-login-screen"
              className="btn-primary text-base px-8 py-3.5 glow-primary"
            >
              Start 14-Day Free Trial
              <ArrowRight size={18} />
            </Link>
            <button
              onClick={handleLiveDemo}
              disabled={demoLoading}
              className="btn-secondary text-base px-8 py-3.5 group"
            >
              {demoLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Play size={14} className="text-primary fill-primary ml-0.5" />
                  </div>
                  {live ? 'End Demo Call' : 'Live Demo Call'}
                </>
              )}
            </button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
            {statsData?.map((stat) => (
              <div key={`stat-${stat?.value}`} className="text-center">
                <div className="text-3xl font-extrabold text-foreground tabular-nums mb-1">
                  {stat?.value}
                </div>
                <div className="text-sm font-semibold text-foreground">{stat?.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{stat?.sublabel}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard preview mockup */}
        <div className="mt-16 max-w-4xl mx-auto relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-accent/20 rounded-2xl blur-xl opacity-60" />
          <div className="relative card-elevated overflow-hidden border-border/60">
            {/* Mock dashboard header */}
            <div className="flex items-center gap-2 px-4 py-3 bg-secondary/50 border-b border-border">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-negative/60" />
                <div className="w-3 h-3 rounded-full bg-warning/60" />
                <div className="w-3 h-3 rounded-full bg-positive/60" />
              </div>
              <div className="flex-1 mx-4">
                <div className="h-5 bg-background/50 rounded-md flex items-center px-3">
                  <span className="text-xs text-muted-foreground font-mono">app.voicelink.ai/dashboard</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-positive/10 border border-positive/20">
                <div className="w-2 h-2 rounded-full bg-positive animate-pulse" />
                <span className="text-xs font-medium text-positive">AI Active</span>
              </div>
            </div>

            {/* Mock dashboard content */}
            <div className="p-4 bg-background/50">
              <div className="grid grid-cols-4 gap-3 mb-4">
                {[
                  { label: 'Calls Today', value: '47', trend: '+12%', positive: true },
                  { label: 'Avg Sentiment', value: '8.4/10', trend: '+0.6', positive: true },
                  { label: 'SMS Sent', value: '31', trend: '66% rate', positive: true },
                  { label: 'Missed Calls', value: '2', trend: '-8 vs avg', positive: false },
                ]?.map((card) => (
                  <div key={`mock-${card?.label}`} className="card-elevated p-3">
                    <div className="text-xs text-muted-foreground mb-1">{card?.label}</div>
                    <div className="text-xl font-bold text-foreground tabular-nums">{card?.value}</div>
                    <div className={`text-xs mt-1 font-medium ${card?.positive ? 'text-positive' : 'text-negative'}`}>
                      {card?.trend}
                    </div>
                  </div>
                ))}
              </div>

              {/* Mock call log rows */}
              <div className="card-elevated overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
                  <span className="text-xs font-semibold text-foreground">Recent Calls</span>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-positive animate-pulse" />
                    Live
                  </div>
                </div>
                {[
                  { caller: '+1 555 010 1234', duration: '3m 42s', sentiment: '😊 Positive', summary: 'Appointment booking for tomorrow 11am' },
                  { caller: '+1 555 010 2345', duration: '1m 18s', sentiment: '😐 Neutral', summary: 'Business hours inquiry, SMS sent' },
                  { caller: '+1 555 010 3456', duration: '5m 03s', sentiment: '😟 Frustrated', summary: 'Order delay complaint — escalation flagged' },
                ]?.map((row, i) => (
                  <div key={`mock-row-${i}`} className="flex items-center gap-4 px-4 py-2.5 border-b border-border/50 last:border-0 text-xs">
                    <span className="font-mono text-muted-foreground w-32 flex-shrink-0">{row?.caller}</span>
                    <span className="font-mono text-foreground w-16">{row?.duration}</span>
                    <span className="w-24">{row?.sentiment}</span>
                    <span className="text-muted-foreground flex-1 truncate">{row?.summary}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
